import { useState, useEffect, useCallback } from "react";
import { X, Loader2, ArrowDownUp, MessageCircle } from "lucide-react";

const ALL_COINS = {
  btc:  { name: "Bitcoin",   symbol: "BTC",  id: "bitcoin",       decimals: 8  },
  eth:  { name: "Ethereum",  symbol: "ETH",  id: "ethereum",      decimals: 18 },
  usdt: { name: "Tether",    symbol: "USDT", id: "tether",        decimals: 6  },
  bnb:  { name: "BNB",       symbol: "BNB",  id: "binancecoin",   decimals: 18 },
  sol:  { name: "Solana",    symbol: "SOL",  id: "solana",        decimals: 9  },
  xrp:  { name: "XRP",       symbol: "XRP",  id: "ripple",        decimals: 6  },
};

export default function SwapModal({ isOpen, onClose, holdings = [] }) {
  const [fromCoin, setFromCoin] = useState("");
  const [toCoin, setToCoin] = useState("");
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("select");
  const [rate, setRate] = useState("");
  const [loadingRate, setLoadingRate] = useState(false);

  const getBalance = (coinId) => {
    const holding = holdings.find(h => h.symbol?.toLowerCase() === coinId.toLowerCase());
    return holding?.amount || 0;
  };

  const fromBalance = getBalance(fromCoin);

  // REAL RATES FOR ALL COINS using CoinGecko
  const fetchRealRate = useCallback(async () => {
    if (!fromCoin || !toCoin || !amount || Number(amount) <= 0) {
      setRate("");
      return;
    }

    setLoadingRate(true);

    try {
      const fromId = ALL_COINS[fromCoin].id;
      const toId = ALL_COINS[toCoin].id;

      const res = await fetch(
        `https://api.coingecko.com/api/v3/simple/price?ids=${fromId},${toId}&vs_currencies=usd`
      );
      const data = await res.json();

      const fromPrice = data[fromId]?.usd || 0;
      const toPrice = data[toId]?.usd || 0;

      if (fromPrice === 0 || toPrice === 0) throw new Error("Price missing");

      const receiveAmount = (Number(amount) * fromPrice / toPrice) * 0.985; // 1.5% fee
      setRate(receiveAmount.toFixed(8));
    } catch (err) {
      // Final fallback
      setRate((Number(amount) * 0.98).toFixed(8));
    } finally {
      setLoadingRate(false);
    }
  }, [fromCoin, toCoin, amount]);

  useEffect(() => {
    const timer = setTimeout(() => fetchRealRate(), 500);
    return () => clearTimeout(timer);
  }, [fromCoin, toCoin, amount, fetchRealRate]);

  const handleSwap = () => {
    if (Number(amount) > fromBalance) return;
    setStep("loading");
    setTimeout(() => setStep("fee-required"), 2500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">

        <div className="bg-gradient-to-r from-primary to-indigo-600 text-white p-5 text-center relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full">
            <X className="w-6 h-6" />
          </button>
          <h2 className="text-2xl font-bold">Swap Crypto</h2>
          <p className="text-sm opacity-90 mt-1">Real-time rates • 1.5% fee</p>
        </div>

        <div className="p-5 md:p-6 flex-1 overflow-y-auto">

          {step === "select" && (
            <>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">You Send</label>
                <select
                  value={fromCoin}
                  onChange={(e) => setFromCoin(e.target.value)}
                  className="w-full px-4 py-4 text-lg border border-gray-300 rounded-2xl focus:ring-2 focus-ring-primary focus:outline-none"
                >
                  <option value="">Select coin</option>
                  {Object.entries(ALL_COINS).map(([id, coin]) => {
                    const balance = getBalance(id);
                    return (
                      <option key={id} value={id}>
                        {coin.name} — {balance} {coin.symbol}
                      </option>
                    );
                  })}
                </select>
              </div>

              {fromCoin && (
                <div className="mb-5">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span className="font-medium">Amount</span>
                    <span>Balance: {fromBalance.toFixed(6)} {fromCoin.toUpperCase()}</span>
                  </div>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    step="any"
                    className="w-full px-4 py-4 text-lg font-medium border border-gray-300 rounded-2xl focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
              )}

              <div className="flex justify-center my-6">
                <div className="p-3 bg-primary/10 rounded-full">
                  <ArrowDownUp className="w-7 h-7 text-primary" />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">You Receive</label>
                <select
                  value={toCoin}
                  onChange={(e) => setToCoin(e.target.value)}
                  disabled={!fromCoin}
                  className="w-full px-4 py-4 text-lg border border-gray-300 rounded-2xl focus:ring-2 focus:ring-primary focus:outline-none disabled:bg-gray-50"
                >
                  <option value="">Select coin</option>
                  {Object.entries(ALL_COINS)
                    .filter(([id]) => id !== fromCoin)
                    .map(([id, coin]) => (
                      <option key={id} value={id}>{coin.name}</option>
                    ))}
                </select>

                {amount && toCoin && (
                  <div className="mt-5 p-5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl text-center border border-green-200">
                    {loadingRate ? (
                      <Loader2 className="w-10 h-10 text-green-600 animate-spin mx-auto" />
                    ) : (
                      <>
                        <p className="text-sm font-medium text-green-700">You will receive</p>
                        <p className="text-3xl font-bold text-green-800 mt-2">
                          {rate} {toCoin.toUpperCase()}
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={handleSwap}
                disabled={!fromCoin || !toCoin || !amount || Number(amount) <= 0 || Number(amount) > fromBalance}
                className={`w-full py-4 rounded-2xl font-bold text-white text-lg transition-all shadow-lg
                  ${!fromCoin || !toCoin || !amount || Number(amount) > fromBalance
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90 active:scale-95"
                  }`}
              >
                {Number(amount) > fromBalance ? "Insufficient Balance" : "Swap Now"}
              </button>
            </>
          )}

          {step === "loading" && (
            <div className="text-center py-20">
              <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
              <p className="text-xl font-semibold text-gray-700">Processing your swap...</p>
            </div>
          )}

          {step === "fee-required" && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-yellow-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                <MessageCircle className="w-12 h-12 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4">ETH Gas Fee Required</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                You need <strong>ETH</strong> in your wallet to pay network fees.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
                <p className="text-sm text-amber-800">
                  Contact support to add ETH and complete your swap.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button onClick={onClose} className="py-3 rounded-xl border border-gray-300 font-medium">
                  Close
                </button>
                <a href="mailto:support@yourapp.com" className="py-3 rounded-xl bg-primary text-white font-bold flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Contact Support
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}